import React from 'react'
import PropTypes from 'prop-types'
import {TableBody, TableRow, TableCell, Checkbox} from '@mui/material'
import EmptyState from '@/components/molecules/EmptyState/EmptyState'
import ShimmerTableRow from '@/components/atoms/ShimmerTableRow'
import {LinkText, Tag} from '@/components/atoms'
import {COLOR_CODE, TAG_COLOR} from '@/utils/constants/colors'

import {getISOTimeString, getFormattedDate} from '@/utils/commons'
/**
 * Pure presentational table body for CustomDataTable.
 * All business logic (links, downloads, formatting) must be provided via column config or parent/handler.
 *
 * Example column config usage:
 * {
 *   name_of_db_column: 'salesTargetName',
 *   render: (row, column, rowIndex, colIndex) => <LinkText ... />, // full custom renderer
 *   linkHandler: (row) => { ... }, // for clickable text
 *   linkLabel: (row) => row.salesTargetName, // for clickable text label
 *   downloadHandler: (row) => { ... }, // for download link
 *   downloadLabel: () => 'Download', // for download link label
 * }
 */
// Helper: Determine if row is selected

// Common helper to get row ID
const getRowId = row => row.rewardId ?? row.id ?? row.uuid ?? row.productId ?? row.storeId;

const isRowSelected = (row, selectedRows, partialSelectAll, deselectedItems) => {
  const rowId = getRowId(row);
  if (!rowId) return false;
  if (partialSelectAll) {
    return !deselectedItems?.has(rowId)
  }
  return selectedRows.some(selectedRow => {
    const selectedId = getRowId(selectedRow)
    return selectedId === rowId
  })
}

// Helper: Render checkbox cell
const renderCheckbox = (row, selectedRows, partialSelectAll, deselectedItems, handleRowSelect, hideCheckbox = false) => (
  <TableCell sx={{width: '60px'}}>
    {!hideCheckbox && (
      <Checkbox
        size="small"
        className="custom-checkbox"
        checked={isRowSelected(row, selectedRows, partialSelectAll, deselectedItems)}
        onChange={() => handleRowSelect(row)}
      />
    )}
  </TableCell>
)

const transformDate = (row, columnName) => {
  const value = row[columnName]
  if (value == null || value === undefined || value == '—') {
    row[columnName] = '—'
  }
  try {
    const isoTimeStamp = getISOTimeString(value)
    const formattedDate = getFormattedDate(isoTimeStamp, false, '-', 'yyyy-mm-dd')
    row[columnName] = formattedDate
  } catch (error) {
    return
  }
}
// Helper: Render cell content
const renderCellContent = (row, column, rowIndex, colIndex) => {
  const dateColumns = new Set([
  'startDate',
  'endDate',
  'lastUpdatedDate',
  'createdAt',
  'scheduledAt',
  'updatedDate',
]);

  if (typeof column.render === 'function') {
    return column.render(row, column, rowIndex, colIndex)
  }

  if (column.linkHandler) {
    return renderLink(row, column, rowIndex, colIndex)
  }

  if (column.downloadHandler) {
    return renderDownload(row, column, rowIndex, colIndex)
  }

  if (column.name_of_db_column === 'includeExclude') {
    return renderIncludeExclude(row)
  }
  if (column.name_of_db_column === 'rewardAcceptance') {
    return renderRewardAcceptance(row)
  }

  if (dateColumns.has(column.name_of_db_column)) {
     transformDate(row, column.name_of_db_column);
  }
  return renderDefault(row, column)
}

const renderLink = (row, column, rowIndex, colIndex) => (
  <LinkText
    text={
      column.linkLabel ? column.linkLabel(row, column, rowIndex, colIndex) : row[column.name_of_db_column] || '\u2014'
    }
    id={`link-${rowIndex}-${column.name_of_db_column}`}
    handleClick={() => column.linkHandler(row, column, rowIndex, colIndex)}
  />
)

const renderDownload = (row, column, rowIndex, colIndex) => {
  if (row.status === 'Draft') {
    return (
      <div className="text-gray-500">
        {column.downloadLabel ? column.downloadLabel(row, column, rowIndex, colIndex) : 'Download'}
      </div>
    )
  }
  return (
    <div>
      <LinkText
        text={
          column.downloadLabel
            ? column.downloadLabel(row, column, rowIndex, colIndex)
            : row[column.name_of_db_column] || '\u2014'
        }
        id={`link-${rowIndex}-${column.name_of_db_column}`}
        handleClick={() => column.downloadHandler(row, column, rowIndex, colIndex)}
        data-testid="link-text"
      />
    </div>
  )
}

const renderIncludeExclude = row => {
  if (row?.included === undefined) {
    return <span>—</span>
  }
  return (
    <Tag
      text={row.included ? 'Exclude' : 'Include'}
      color={row.included ? TAG_COLOR.RED_150 : TAG_COLOR.GREEN_150}
      onClick={() => {}} // Placeholder for actual click handler
    />
  )
}

const renderDefault = (row, column) => (
  <div
    style={{
      overflow: 'hidden',
      width: '100%',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    }}
  >
    {row[column.name_of_db_column] === null || row[column.name_of_db_column] === undefined
      ? '—'
      : row[column.name_of_db_column]}
  </div>
)

const renderRewardAcceptance = row => {
  const isLowAcceptance = row?.rewardAcceptance <= 75

  return (
    <div
      style={
        isLowAcceptance
          ? {
              fontWeight: '400',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex',
              backgroundColor: COLOR_CODE._DB3333,
              width: 'fit-content',
            }
          : {}
      }
    >
      {row?.rewardAcceptance + '%' ?? '—'}
    </div>
  )
}

// Helper: Render a table row
const renderTableRow = (
  row,
  rowIndex,
  columns,
  selectedRows = [],
  partialSelectAll,
  deselectedItems,
  handleRowSelect,
  key,
  hideSelectAll,
  hideCheckbox = false
) => (
  <TableRow key={key} selected={isRowSelected(row, selectedRows, partialSelectAll, deselectedItems)}>
    {/* If both hideSelectAll and hideCheckbox are true, hide the entire cell */}
    {!(hideSelectAll && hideCheckbox) && renderCheckbox(row, selectedRows, partialSelectAll, deselectedItems, handleRowSelect, hideCheckbox)}
    {columns.map((column, colIndex) => {

      const effectiveMinWidth = column?.minWidth
        ? column.minWidth
        :  210;

      return (
        <TableCell
          className="custom-data-table-cell"
          key={`cell-${colIndex + 1}`}
          align="left"
          sx={effectiveMinWidth ? { minWidth: effectiveMinWidth } : undefined}
        >
          {renderCellContent(row, column, rowIndex, colIndex)}
        </TableCell>
      );
    })}
    {/* If both hideSelectAll and hideCheckbox are true, hide the trailing cell */}
    {!(hideSelectAll && hideCheckbox) && <TableCell sx={{width: '60px'}} />}
  </TableRow>
)

const DataTableBody = ({
  isLoading,
  records,
  columns,
  selectedRows,
  handleRowSelect,
  handleResetAll,
  partialSelectAll,
  deselectedItems,
  hideSelectAll = false,
  hideCheckbox = false,
  emptyStateConfig = {},
}) => {
  // Calculate total columns: columns + leading cell + trailing cell
  // If both hideSelectAll and hideCheckbox are true, we hide both leading and trailing cells
  const totalColumns = columns.length + ((hideSelectAll && hideCheckbox) ? 0 : 2);
  if (isLoading) {
    return (
      <TableBody>
        {Array.from({length: 15}).map((_, index) => (
          <ShimmerTableRow
            key={`loading-${index + 1}`}
            columnCount={totalColumns}
            hideSelectAll={hideSelectAll}
            hideCheckbox={hideCheckbox}
          />
        ))}
      </TableBody>
    )
  }
  if (records?.length === 0) {
    return (
      <TableBody>
        <TableRow className="empty-state-row">
          <TableCell
            colSpan={totalColumns}
            align="center"
            sx={{
              backgroundColor: COLOR_CODE._1D1D1D,
              '&:hover': {
                backgroundColor: COLOR_CODE._1D1D1D,
              },
              '& .MuiTableRow-root:hover': {
                backgroundColor: COLOR_CODE._1D1D1D,
              },
            }}
          >
            <EmptyState
              onActionClick={handleResetAll}
              title={emptyStateConfig.title}
              description={emptyStateConfig.description}
              buttonText={emptyStateConfig.buttonText}
              hideTitle={emptyStateConfig.hideTitle}
              hideDescription={emptyStateConfig.hideDescription}
              hideButton={emptyStateConfig.hideButton}
              titleTextStyle={emptyStateConfig.titleTextStyle}
              hideIcon={emptyStateConfig.hideIcon}
              customStyle={emptyStateConfig.customStyle}
            />
          </TableCell>
        </TableRow>
      </TableBody>
    )
  }
  return (
    <TableBody>
      {records.map((row, rowIndex) =>
        renderTableRow(
          row,
          rowIndex,
          columns,
          selectedRows,
          partialSelectAll,
          deselectedItems,
          handleRowSelect,
          row.rewardId ?? row.id ?? row.uuid ?? row.productId ?? rowIndex,   // Use rewardId as primary unique key
          hideSelectAll,
          hideCheckbox
        )
      )}
    </TableBody>
  )
}

DataTableBody.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  records: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  selectedRows: PropTypes.array.isRequired,
  handleRowSelect: PropTypes.func.isRequired,
  handleResetAll: PropTypes.func.isRequired,
  partialSelectAll: PropTypes.bool,
  deselectedItems: PropTypes.instanceOf(Set),
  hideSelectAll: PropTypes.bool,
  hideCheckbox: PropTypes.bool,
  emptyStateConfig: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    buttonText: PropTypes.string,
    hideTitle: PropTypes.bool,
    hideDescription: PropTypes.bool,
    hideButton: PropTypes.bool,
    hideIcon: PropTypes.bool,
  }),
}

export default DataTableBody
